import React, { useState } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import FilterSection from './FilterSection';
import FilterSelect from './FilterSelect';

interface AnimatronicFiltersProps {
  onSearchChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onParkChange: (value: string) => void;
  onAttractionChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onBrandChange: (value: string) => void;
  countries: string[];
  parks: string[];
  attractions: string[];
  types: string[];
  statuses: string[];
  brands: string[];
  filters: {
    search: string;
    country?: string;
    park?: string;
    attraction?: string;
    type?: string;
    status?: string;
    brand?: string;
  };
}

const AnimatronicFilters: React.FC<AnimatronicFiltersProps> = ({
  onSearchChange,
  onCountryChange,
  onParkChange,
  onAttractionChange,
  onTypeChange,
  onStatusChange,
  onBrandChange,
  countries,
  parks,
  attractions,
  types,
  statuses,
  brands,
  filters,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-background-secondary rounded-lg border border-gray-800 p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="space-y-4">
        {/* Search and Filter Toggle */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-grow relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-secondary" />
            <input
              type="text"
              placeholder="Search animatronics..."
              value={filters.search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-background border border-gray-800 rounded-lg text-content-primary placeholder-content-secondary focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
            />
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center px-4 py-2.5 bg-background border border-gray-800 rounded-lg text-content-primary hover:bg-background-secondary transition-colors sm:w-auto w-full"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            {isExpanded ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {/* Expanded Filters */}
        {isExpanded && (
          <div className="pt-4 border-t border-gray-800">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Location Filters */}
              <div className="space-y-4">
                <FilterSection title="Location">
                  <FilterSelect
                    value={filters.country || ''}
                    onChange={onCountryChange}
                    options={[
                      { value: '', label: 'All Countries' },
                      ...countries.map(country => ({
                        value: country,
                        label: country,
                      })),
                    ]}
                  />
                </FilterSection>

                <FilterSection title="Park">
                  <FilterSelect
                    value={filters.park || ''}
                    onChange={onParkChange}
                    options={[
                      { value: '', label: 'All Parks' },
                      ...parks.map(park => ({
                        value: park,
                        label: park,
                      })),
                    ]}
                  />
                </FilterSection>
              </div>

              <div className="space-y-4">
                <FilterSection title="Attraction">
                  <FilterSelect
                    value={filters.attraction || ''}
                    onChange={onAttractionChange}
                    options={[
                      { value: '', label: 'All Attractions' },
                      ...attractions.map(attraction => ({
                        value: attraction,
                        label: attraction,
                      })),
                    ]}
                  />
                </FilterSection>

                <FilterSection title="Type">
                  <FilterSelect
                    value={filters.type || ''}
                    onChange={onTypeChange}
                    options={[
                      { value: '', label: 'All Types' },
                      ...types.map(type => ({
                        value: type,
                        label: type.split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                      })),
                    ]}
                  />
                </FilterSection>
              </div>

              <div className="space-y-4">
                <FilterSection title="Status">
                  <FilterSelect
                    value={filters.status || ''}
                    onChange={onStatusChange}
                    options={[
                      { value: '', label: 'All Statuses' },
                      ...statuses.map(status => ({
                        value: status,
                        label: status.split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                      })),
                    ]}
                  />
                </FilterSection>

                <FilterSection title="Brand">
                  <FilterSelect
                    value={filters.brand || ''}
                    onChange={onBrandChange}
                    options={[
                      { value: '', label: 'All Brands' },
                      ...brands.map(brand => ({
                        value: brand,
                        label: brand,
                      })),
                    ]}
                  />
                </FilterSection>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimatronicFilters;